/**
 * Environmental Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * EnvironmentalSettingsPage (Temperature, Humidity, Battery tabs).
 *
 * Data table: core_environmental_monitoring_control (PK: control_id)
 *
 * environment_type_id:  1 = Temperature, 2 = Humidity, 3 = Battery
 * environmental_alarm_type_id:  1 = Global, 2 = Occupied, 3 = Unoccupied, 4 = Custom
 *
 * Threshold values stored as-is (°F for temp, % for humidity, V for battery).
 * test_after stored in seconds; UI displays as minutes (÷60).
 * device_ids stored as comma-separated string of device IDs.
 */

import { apiFetch, apiSave } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch environment control records by type for a campus. */
export function fetchEnvironmentControls(campusId, envTypeId) {
    return apiFetch({
        procedure: 'sp_dev_getEnvironmentControls',
        reqType: 'stored',
        payload: {
            campus_id: campusId,
            environment_type_filter: envTypeId,
        },
    });
}

/** Fetch Inovonics devices filtered by device category for a campus. */
export function fetchEnvironmentDevices(campusId, categoryFilter) {
    return apiFetch({
        procedure: 'sp_dev_getInovonicsDevices',
        reqType: 'stored',
        payload: {
            device_filter: '',
            device_category_filter: categoryFilter,
            campus_id: campusId,
        },
    });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/** Add a new environment control record. */
export function addEnvironmentControl(payload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_environmental_monitoring_control',
        payload,
        campusId,
        userId,
        clientId,
        method: 'PUT',
    });
}

/** Edit an existing environment control record. */
export function editEnvironmentControl(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_environmental_monitoring_control',
        payload,
        oldPayload,
        pKey: 'control_id',
        condition: 'primary',
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}

/** Delete a single environment control record. */
export function deleteEnvironmentControl(controlId, userId, { campusId, clientId }) {
    return apiSave({
        table: 'core_environmental_monitoring_control',
        payload: [{ control_id: controlId, modified_by: userId }],
        pKey: 'control_id',
        condition: 'primary',
        campusId,
        userId,
        clientId,
        method: 'DELETE',
    });
}

/** Delete multiple environment control records sequentially. */
export async function deleteEnvironmentControls(controlIds, userId, meta) {
    for (const id of controlIds) {
        const [ok, err] = await deleteEnvironmentControl(id, userId, meta);
        if (!ok) return [false, err];
    }
    return [true];
}

// ---------------------------------------------------------------------------
// CREATE DEFAULTS
// ---------------------------------------------------------------------------

/**
 * Create default control records for a given environment type.
 *
 * Temperature (1): Occupied Areas + Unoccupied Areas
 * Humidity (2):    Occupied Areas + Unoccupied Areas
 * Battery (3):     Global Settings
 */
export function createDefaultControls(envTypeId, { campusId, userId, clientId }) {
    let payload;

    if (envTypeId === 1) {
        // Temperature defaults
        payload = [
            {
                description: 'Occupied Areas',
                low_alarm: 65,
                high_alarm: 78,
                environmental_alarm_type_id: 2,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 1,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
            {
                description: 'Unoccupied Areas',
                low_alarm: 55,
                high_alarm: 90,
                environmental_alarm_type_id: 3,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 1,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    } else if (envTypeId === 2) {
        // Humidity defaults
        payload = [
            {
                description: 'Occupied Areas',
                low_alarm: 65,
                high_alarm: 78,
                environmental_alarm_type_id: 2,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 2,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
            {
                description: 'Unoccupied Areas',
                low_alarm: 55,
                high_alarm: 90,
                environmental_alarm_type_id: 3,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 2,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    } else {
        // Battery default
        payload = [
            {
                description: 'Global Settings',
                low_alarm: 2.4,
                environmental_alarm_type_id: 1,
                alarm_on: 1,
                campus_id: campusId,
                environmental_type_id: 3,
                test_after: 3600,
                device_ids: '',
                modified_by: userId,
            },
        ];
    }

    return apiSave({
        table: 'core_environmental_monitoring_control',
        payload,
        campusId,
        userId,
        clientId,
        method: 'PUT',
    });
}
